/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { useQueries } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import type { Coordinates, SoundingsCollection } from '@opengeoweb/shared';
import {
  getCollectionDetails,
  getCollectionInstances,
  getCollectionLocations,
  getEdrCollectionForLocation,
  getEdrCollectionForPosition,
} from './api';
import type {
  CollectionDetails,
  SoundingFeature,
  SoundingsInstances,
} from './types';
import {
  parseCoverageCollection,
  getTimestepsFromInstanceOrCollection,
  buildIntervalDatetime,
} from './utils';
import type { ServiceWithLocations } from '../../components/MenuBar/LocationsMenu';
import type { SoundingsCollectionsWithInstances } from '../../components/Soundings/types';
import type { SourceType } from '../../store/types';

export const sortSoundingFeatures = (
  data: SoundingFeature[],
): SoundingFeature[] => {
  return data.sort((featureA, featureB) => {
    // Sort by date (ascending)
    if (featureA.properties.time !== featureB.properties.time) {
      return featureA.properties.time > featureB.properties.time ? 1 : -1;
    }
    // Sort by pressure (descending)
    return featureB.properties.pressure - featureA.properties.pressure;
  });
};

const constructQueryParams = (
  collectionId: string,
  baseUrl: string,
  instanceId: string | undefined,
  location: string | Coordinates,
  datetime: string,
  parameterMappings: Record<string, string>,
  windSpeedUnit: string,
  verticalRange: string,
): { queryKey: string[]; queryFn: () => Promise<SoundingFeature[]> } => {
  const parameters = getParameterMappings(parameterMappings);
  if (typeof location === 'string') {
    // LOCATION call
    return {
      queryKey: [
        'edr-soundings-for-location',
        collectionId,
        instanceId || '',
        location,
        parameters.join(),
        datetime,
        verticalRange,
      ],
      queryFn: async (): Promise<SoundingFeature[]> => {
        // No collection, location or time selected, don't do EDR call but return empty array
        if (collectionId === undefined || location === '' || datetime === '') {
          return [];
        }

        try {
          const rawData = await getEdrCollectionForLocation(
            collectionId,
            baseUrl,
            instanceId,
            location,
            parameters,
            datetime,
            verticalRange,
          );
          const parsedData = parseCoverageCollection(
            rawData,
            parameterMappings,
            windSpeedUnit,
          );

          return sortSoundingFeatures(parsedData);
        } catch (_err) {
          throw new Error('Error fetching EDR data collection by location');
        }
      },
    };
  }
  // POSITION call
  return {
    queryKey: [
      'edr-soundings-for-position',
      collectionId,
      location.join(),
      parameters.join(),
      datetime,
      verticalRange,
    ],
    queryFn: async (): Promise<SoundingFeature[]> => {
      // No collection, position or time selected, don't do EDR call but return empty array
      if (
        collectionId === undefined ||
        location === undefined ||
        datetime === ''
      ) {
        return [];
      }

      try {
        const rawData = await getEdrCollectionForPosition(
          collectionId,
          baseUrl,
          instanceId,
          location,
          parameters,
          datetime,
          verticalRange,
        );
        const parsedData = parseCoverageCollection(
          rawData,
          parameterMappings,
          windSpeedUnit,
        );

        return sortSoundingFeatures(parsedData);
      } catch (_err) {
        throw new Error('Error fetching EDR data collection by position');
      }
    },
  };
};

export const defaultParameterMappings = {
  temperature: 'temperature',
  windDirection: 'winddirection',
  windSpeed: 'windspeedms',
  windU: 'windums',
  windV: 'windvms',
  pressure: 'pressure',
  dewpoint: 'dewpoint',
  humidity: 'humidity',
  specificHumidity: 'specifichumidity',
  relativeHumidity: 'relativehumidity',
};

export const getParameterMappings = (
  parameterMappings: Record<string, string>,
): string[] => {
  return Object.keys(parameterMappings).map((key) => parameterMappings[key]);
};

export const useMultipleSources = (
  sources: SourceType[],
  locations: Record<string, string | undefined | Coordinates>,
  datetime?: string,
): {
  data: SoundingFeature[][];
  isPending: boolean;
  error: Error | null | undefined;
} => {
  const {
    data: timesteps,
    error: timestepsError,
    isPending: timestepsPending,
  } = useTimesteps(sources);
  const queries = useMemo(() => {
    return sources.map(
      (
        {
          collectionId,
          url,
          instanceId,
          parameterMappings,
          windSpeedUnit,
          verticalRange,
          prefetchAllTimesteps,
        },
        index,
      ) => {
        const sourceTimesteps = timesteps[index] ?? [];
        const location = locations[collectionId] || '';
        const resolvedParamMappings =
          parameterMappings || defaultParameterMappings;
        const resolvedWindUnit = windSpeedUnit || 'ms';
        const resolvedVerticalRange = verticalRange || '';

        // Prefetch path: fetch the entire time range once, then select the right timestep
        if (prefetchAllTimesteps && sourceTimesteps.length > 0) {
          const intervalDatetime = buildIntervalDatetime(sourceTimesteps);

          let timeToShow = '';
          if (datetime) {
            const requestedTime = new Date(datetime).getTime();
            if (Number.isFinite(requestedTime)) {
              timeToShow =
                sourceTimesteps.find(
                  (t) => new Date(t).getTime() === requestedTime,
                ) ?? '';
            }
          }

          return {
            ...constructQueryParams(
              collectionId,
              url,
              instanceId,
              location,
              intervalDatetime,
              resolvedParamMappings,
              resolvedWindUnit,
              resolvedVerticalRange,
            ),
            select: (allFeatures: SoundingFeature[]): SoundingFeature[] => {
              if (!timeToShow || allFeatures.length === 0) {
                return [];
              }
              const timeToShowMs = new Date(timeToShow).getTime();
              return allFeatures.filter(
                (feat) =>
                  new Date(feat.properties.time).getTime() === timeToShowMs,
              );
            },
          };
        }

        // Per-timestep path: one fetch per selected time
        let dateToUse = '';
        if (datetime && sourceTimesteps.length > 0) {
          const requestedTime = new Date(datetime).getTime();
          if (Number.isFinite(requestedTime)) {
            const exactMatch = sourceTimesteps.find(
              (timestep) => new Date(timestep).getTime() === requestedTime,
            );

            dateToUse = exactMatch || '';
          }
        }

        return constructQueryParams(
          collectionId,
          url,
          instanceId,
          location,
          dateToUse,
          resolvedParamMappings,
          resolvedWindUnit,
          resolvedVerticalRange,
        );
      },
    );
  }, [datetime, locations, sources, timesteps]);

  return useQueries({
    queries,
    combine: (queryResults) => {
      const data = queryResults.map((result) => {
        return result.data || [];
      });
      const isPending =
        timestepsPending || queryResults.some((result) => result.isPending);
      const error =
        timestepsError || queryResults.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });
};

export const useTimesteps = (
  sourceSelections: SourceType[],
): { data: string[][]; error: Error | null; isPending: boolean } => {
  const queries = React.useMemo(
    () =>
      sourceSelections.map(({ collectionId, instanceId, url }) => ({
        queryKey: ['edr-soundings-instances', collectionId],
        queryFn: async (): Promise<SoundingsInstances | CollectionDetails> => {
          // If instance id passed, use this to fetch time extent
          if (instanceId && instanceId !== '') {
            const data = await getCollectionInstances(collectionId, url);
            return data;
          }
          // Otherwise, use collection details
          const data = await getCollectionDetails(collectionId, url);
          return data;
        },
        select: (data: SoundingsInstances | CollectionDetails): string[] => {
          if ((data as SoundingsInstances).instances !== undefined) {
            const instance = (data as SoundingsInstances).instances.find(
              (instance) => instance.id === instanceId,
            );
            return getTimestepsFromInstanceOrCollection(instance);
          }
          return getTimestepsFromInstanceOrCollection(
            data as CollectionDetails,
          );
        },
        refetchInterval: 5 * 60 * 1000,
      })),
    [sourceSelections],
  );

  const results = useQueries({
    queries,
    combine: (queryResults) => {
      const data = queryResults.map((result) => result.data || []);
      // The underlying query is generic, so we rewrap the error to be more specific
      const foundError = queryResults.find((result) => result?.error)?.error;
      const error = foundError
        ? new Error('Error fetching timesteps by collection', {
            cause: foundError,
          })
        : null;
      const isPending = queryResults.some((result) => result.isPending);

      return { data, error, isPending };
    },
  });

  return results;
};

export const useGetCollectionsInstances = (
  collections?: SoundingsCollection[],
  isDialogOpen?: boolean,
): {
  data: SoundingsCollectionsWithInstances[] | undefined;
  isPending: boolean;
  error: Error | null | undefined;
} => {
  const collectionIds = useMemo(
    () =>
      collections?.map((collection) => {
        return { id: collection.id, url: collection.url };
      }) || [],
    [collections],
  );

  // Fetch collection details
  const queriesCollection = useMemo(
    () =>
      collectionIds.map(({ id, url }) => {
        return {
          queryKey: ['edr-soundings-collection-details', id, url],
          queryFn: async (): Promise<CollectionDetails> => {
            const data = await getCollectionDetails(id, url);
            return data;
          },
          enabled: isDialogOpen,
        };
      }),
    [collectionIds, isDialogOpen],
  );

  const resultsCollection = useQueries({
    queries: queriesCollection,
    combine: (queryResults) => {
      const data = queryResults.map((result) => {
        return result.data?.data_queries?.instances !== undefined;
      });
      const isPending = queryResults.some((result) => result.isPending);
      const error = queryResults.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });

  // Fetch instance details for collections that have instances
  const queries = useMemo(() => {
    return collectionIds.map(({ id, url }, index) => {
      return {
        queryKey: ['edr-soundings-instances', id, resultsCollection.data],
        queryFn: async (): Promise<SoundingsInstances> => {
          // Only fetch if there are instances for this collection

          if (resultsCollection.data[index]) {
            const data = await getCollectionInstances(id, url);
            return data;
          }
          return { instances: [] };
        },
        enabled: isDialogOpen && resultsCollection.data !== undefined,
      };
    });
  }, [collectionIds, isDialogOpen, resultsCollection]);

  const results = useQueries({
    queries,
    combine: (queryResults) => {
      const data = queryResults.map((result) => {
        const instances =
          result.data?.instances.sort((featureA, featureB) =>
            featureB.id.localeCompare(featureA.id),
          ) || []; // Sort to get latest first
        return instances;
      });

      const isPending = queryResults.some((result) => result.isPending);
      const error = queryResults.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });

  const dataWithCollectionInfo = collections?.map((collection, index) => {
    return {
      ...collection,
      instances: results.data[index] || [],
    };
  });

  const errorResult = results.error || resultsCollection.error;
  const isPendingResult = results.isPending || resultsCollection.isPending;

  return {
    ...results,
    isPending: isPendingResult,
    error: errorResult,
    data: dataWithCollectionInfo,
  };
};

export const useGetCollectionLocations = <
  SoundingsCollectionLike extends {
    id: string;
    url: string;
    name: string;
  },
>(
  collections: SoundingsCollectionLike[],
  enabled?: boolean,
): {
  data: ServiceWithLocations[] | undefined;
  isPending: boolean;
  error: Error | null | undefined;
} => {
  const edrQueries = useMemo(
    () =>
      collections?.map((collection) => ({
        queryKey: ['edr-locations', collection.id],
        queryFn: async (): Promise<ServiceWithLocations> => {
          const locations = await getCollectionLocations(
            collection.id,
            collection.url,
          );
          return { ...locations, id: collection.id, name: collection.name };
        },
        enabled,
      })),
    [collections, enabled],
  );

  return useQueries({
    queries: edrQueries,
    combine: (results) => {
      const data = results
        .map((result) => result.data)
        .filter((result) => !!result) as ServiceWithLocations[];
      const isPending = results.some((result) => result.isPending);
      const error = results.find((result) => result?.error)?.error;
      return { data, isPending, error };
    },
  });
};
